/* eslint-disable no-restricted-syntax */
import { useState, useEffect } from "react";

import { Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import Sidenav from "components/Advanced/Sidenav";
import Configurator from "components/Advanced/Configurator";

import theme from "assets/theme";
import themeDark from "assets/theme-dark";

import Router from "routes/routes";

import { useMaterialUIController, setMiniSidenav } from "context";

import lod_ from "lodash";

import myLogo from "assets/images/logo.png";

import "assets/style/app.css";
import "assets/style/error.css";
import "assets/style/navbar.css";
import { useSelector, useDispatch } from "react-redux";
import Login from "pages/login";

import "./assets/fonts/Cedora-Bold.otf";
import "./assets/fonts/Cedora-Regular.otf";
import SnackBarMessage from "components/Custom/SnackBar";
import { buildCustomRoutes } from "routes/utils";
import { CircularProgress, Slide } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import { api } from "redux-react/actions/api";
import { setError } from "redux-react/reducers/errorReducer";
import { initFilter } from "redux-react/reducers/filtersReducers";
import ProfileActions from "redux-react/actions/profileActions";
import FormV2Dialogs from "components/Custom/FormV2Dialogs";

/**
 * @param {*} permissionsExpected The permissions expected to access the element
 * @param {*} permissionsGiven The permissions of the user
 * @returns
 */
export const hasPermissions = (permissionsExpected, permissionsGiven) => {
	if (!permissionsExpected || lod_.isEmpty(permissionsExpected)) return true;

	let hasPermission = false;

	for (let perm of permissionsExpected) {
		if (permissionsGiven.includes(perm)) {
			hasPermission = true;
			break;
		}
	}
	return hasPermission;
};

export default function App() {
	const dispatchEvent = useDispatch();
	const { assistants, user } = useSelector(state => state);

	const [searchParams, setSearchParams] = useSearchParams();
	// Possible search params in URL
	const assistantID = searchParams.get("assistantID");

	const [controller, dispatch] = useMaterialUIController();
	const { miniSidenav, darkMode } = controller;
	const [onMouseEnter, setOnMouseEnter] = useState(false);
	const { pathname } = useLocation();
	const { profile, permissions, loadingAssistant } = useSelector(state => state);
	const [routes, setRoutes] = useState([]);

	const [loading, setLoading] = useState(true);

	const brandName =
		profile?.selectedAssistant?.name ?? process.env.REACT_APP_APPNAME?.toUpperCase();

	// Open sidenav when mouse enter on mini sidenav
	const handleOnMouseEnter = () => {
		if (miniSidenav && !onMouseEnter) {
			setMiniSidenav(dispatch, false);
			setOnMouseEnter(true);
		}
	};

	// Close sidenav when mouse leave mini sidenav
	const handleOnMouseLeave = () => {
		if (onMouseEnter) {
			setMiniSidenav(dispatch, true);
			setOnMouseEnter(false);
		}
	};

	/**
	 * Get all routes
	 */
	const getRoutes = allRoutes => {
		let routes = allRoutes.map((route, index) => {
			if (route.permissions) {
				if (!hasPermissions(route.permissions, permissions.permissions)) {
					return null;
				}
			}

			if (route.collapse) {
				return getRoutes(route.collapse);
			}

			if (route.route && route.type !== "hidden") {
				return <Route exact path={route.route} element={route.component} key={index} />;
			}

			return null;
		});

		let redirectRoute = allRoutes.find(
			route => Boolean(route.route) && !["/login", "/logout"].includes(route.route)
		);

		if (redirectRoute) {
			routes.push();
		}

		return routes;
	};

	/**
	 * Get the redirect route
	 */
	function getRedirectRoute(routes) {
		let redirectRoute = routes.find(
			route => Boolean(route.route) && !["/login", "/logout"].includes(route.route)
		);

		if (redirectRoute) {
			return <Route path="*" element={<Navigate to={redirectRoute.route} />} />;
		}

		return null;
	}

	/**
	 * Select the assistant
	 * @param {object} assistant - The assistant to select
	 * @returns {void} - Nothing
	 */
	function selectProfile(assistant) {
		if (!assistant.active) {
			setLoading(false);
			return;
		}

		dispatchEvent(
			api({
				type: "selectProfile",
				url: "/api/v1/profiles/" + assistant.assistantID,
				onSuccess: res => {
					dispatchEvent(initFilter({ assistantID: res.profile.assistantID }));
					dispatchEvent(ProfileActions.selectProfile(res, assistants, user));
					setLoading(false);
				},
				onFailure: err => {
					dispatchEvent(setError(err.response?.data?.error?.message ?? "none"));
					setLoading(false);
				}
			})
		);
	}

	/**
	 * If user is logged, he needs to chose a profile before to be redirect to dashboard
	 */
	function router() {
		if (!lod_.isEmpty(profile)) {
			return (
				<>
					{getRoutes(routes)}
					{getRedirectRoute(routes)}
				</>
			);
		} else {
			return (
				<>
					<Route exact path="/login" element={<Login />} key="login" />
					<Route path="*" element={<Navigate to="/login" />} />
				</>
			);
		}
	}

	/**
	 * Merge basic routes and custom routes
	 */
	useEffect(() => {
		if (!lod_.isEmpty(profile)) {
			let basicRoutes = Router();
			let customRoutes = profile?.selectedAssistant?.menu ?? [];
			let newRoutes = [...basicRoutes, ...customRoutes];
			newRoutes = buildCustomRoutes(newRoutes);
			setRoutes(newRoutes);
		}
	}, [profile]);

	/**
	 * Scroll to top of the page when changing the route
	 */
	useEffect(() => {
		document.documentElement.scrollTop = 0;
		document.scrollingElement.scrollTop = 0;
	}, [pathname]);

	useEffect(() => {
		setLoading(true);
		// If assistantID match with a possible assistant, select it
		if (!lod_.isNil(assistants[assistantID])) {
			// Select the assistant
			selectProfile(assistants[assistantID]);
		} else {
			setLoading(false);
		}
	}, []);

	if (loading) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					width: "100%",
					height: "100vh"
				}}
			>
				<CircularProgress color="dark" />
			</div>
		);
	}

	return (
		<ThemeProvider theme={darkMode ? themeDark : theme}>
			<CssBaseline />
			<>
				<FormV2Dialogs />
				<SnackBarMessage />
				<Sidenav
					brandName={brandName}
					brand={myLogo}
					routes={routes}
					onMouseEnter={handleOnMouseEnter}
					onMouseLeave={handleOnMouseLeave}
				/>
				<Configurator />
			</>
			<Routes>{router()}</Routes>

			<Slide direction="up" in={!lod_.isEmpty(loadingAssistant)} mountOnEnter unmountOnExit>
				<MDBox bgColor="light" className="loadingBox" display="flex" flexDirection="column">
					<MDBox display="flex" justifyContent="center" alignItems="center">
						<img src={myLogo} alt="logo" width="15%" />
					</MDBox>
					{loadingAssistant?.name && (
						<MDBox mt={10}>
							<MDTypography color="dark" variant="h4" fontWeight="bold">
								{`Connexion sur l'assistant "${loadingAssistant?.name}"`}
							</MDTypography>
						</MDBox>
					)}

					<MDBox mt={5}>
						<CircularProgress color="dark" />
					</MDBox>
				</MDBox>
			</Slide>
		</ThemeProvider>
	);
}
